import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "production",
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

export interface CaptureContext {
  /** Groups every occurrence under one issue regardless of message. */
  fingerprint?: string[];
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

/**
 * Reports an error that code has caught and handled (logged to telemetry,
 * say) but that still belongs in Sentry. A no-op when Sentry isn't
 * initialised, so callers never need to guard it.
 */
export function captureException(
  error: unknown,
  context?: CaptureContext,
): void {
  if (context) {
    Sentry.captureException(error, context);
  } else {
    Sentry.captureException(error);
  }
}

export interface SentryUser {
  id: string;
  accountId: string;
  /** Only set while a site admin is impersonating; null otherwise. */
  actorId: string | null;
}

/**
 * Identity for the Sentry events and traces this browser sends from now
 * on. Ids only, never a name or email address: the id is enough to find
 * the row, and Sentry is not a place to accumulate personal data.
 *
 * Called with the effective user (the impersonated one, while a site admin
 * is impersonating — ADR-0004 §4), which is whose data the session is
 * actually looking at. `actorId` says who is really driving.
 */
export function setSentryUser(user: SentryUser | null): void {
  Sentry.setUser(user ? { id: user.id } : null);
  Sentry.setTag("account_id", user?.accountId);
  Sentry.setTag("actor_user_id", user?.actorId ?? undefined);
}
